import React, { FormEventHandler, useCallback, useRef, useState } from "react";
import axios from "axios";
import { Dialog, FormControl, FormLabel, MenuItem, Select, SlideProps } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { Backspace, Close } from "@mui/icons-material";

import { withStyles } from "tss-react/mui";
import notificationsStyle from "../../../assets/jss/material-dashboard-pro-react/views/notificationsStyle";
import Button from "../../../components/CustomButtons/Button";
import ColorPicker from "../../../components/CustomInput/ColorPicker";
import CustomInput from "../../../components/CustomInput/CustomInput";
import CustomSwitch from "../../../components/CustomInput/CustomSwitch";
import SaveButton from "../../../components/CustomInput/SaveButton";
import GridContainer from "../../../components/Grid/GridContainer";
import GridItem from "../../../components/Grid/GridItem";
import TimeTable from "../../../components/TimeTable/TimeTable";
import TranslationWrapper from "../../../components/TranslationWrapper/TranslationWrapper";
import { convertArrayToSelectOptions } from "../../../templateHelpers";
import { displayOnScreenGroup } from "../../../variables/app";
import { toast } from "react-toastify";
import customSelectStyle from "../../../assets/jss/material-dashboard-pro-react/customSelectStyle";
import jamezzFormStyles from "../../JamezzForm/JamezzFormStyles";
import menukaartenStyle from "../../../assets/jss/material-dashboard-pro-react/views/MenukaartenStyle";
import modalStyle from "../../../assets/jss/material-dashboard-pro-react/modalStyle";

const Transition = React.forwardRef<HTMLDivElement, SlideProps>(function Transition({ children, ...props }, ref) {
  return (
    <Slide direction={"down"} ref={ref} {...props}>
      {children}
    </Slide>
  );
});

const validInputNames = [
  "naam",
  // "imgidbanner", // TODO Kapot in backend
  "is_actief",
  "timeTable",
  "fontColor",
  "bgColor",
  "onOrderscreen",
  "blocked",
  "disableOrdering",
  "sortkey",
] as const;

type ValidInputNameKey = (typeof validInputNames)[number];

function MassEditRow({
  classes,
  tKey,
  tDefault,
  onClick,
  input,
}: {
  classes: {
    [k in string]: string;
  };
  tKey: string;
  tDefault: string;
  onClick: () => void;
  input: JSX.Element;
}) {
  return (
    <GridContainer>
      <GridItem sm={2} xs={12}>
        <FormLabel className={classes.labelHorizontal}>
          <TranslationWrapper tDefault={tDefault} tKey={tKey} />
        </FormLabel>
      </GridItem>
      <GridItem sm={9} xs={12}>
        {input}
      </GridItem>
      <GridItem sm={1} xs={12}>
        <Button
          className={classes.actionButton}
          color={"danger"}
          onClick={onClick}
          simple
          style={{
            margin: 0,
            padding: 0,
          }}
        >
          <Backspace className={classes.icon} />
        </Button>
      </GridItem>
    </GridContainer>
  );
}

function MassEdit(props: {
  open: boolean;
  toggle: () => void;
  refreshTable: () => void;
  menukaartIds: number[];
  classes: {
    [k in string]: string;
  };
}) {
  const { classes } = props;
  const form = useRef<HTMLFormElement | null>(null);
  const [formData, setFormData] = useState<{
    fontColor?: string;
    is_actief?: boolean;
    disableOrdering?: boolean;
    blocked?: boolean;
    bgColor?: string;
    timeTable?: any;
    naam?: string;
    imgidbanner?: string;
    onOrderscreen?: string;
    sortkey?: number;
  }>({});
  const [activeItems, setActiveItems] = useState<{ [k in ValidInputNameKey]?: boolean }>({});
  const [saving, setSaving] = useState(false);
  const [imageList, setImageList] = useState<{ [k in ValidInputNameKey]?: string | ArrayBuffer | null }>({});
  const kitchenDisplay = convertArrayToSelectOptions(displayOnScreenGroup);

  const toggleActiveItem = (field: ValidInputNameKey, state: boolean) => {
    const prevState = { ...activeItems };
    prevState[field] = state;
    setActiveItems(prevState);
  };

  const canSave = () => {
    return (
      validInputNames.filter((input) => {
        return activeItems[input] === undefined || !activeItems[input];
      }).length !== validInputNames.length
    );
  };

  const inputSelect = validInputNames
    .filter((input) => {
      return activeItems[input] === undefined || !activeItems[input];
    })
    .map((input) => {
      return (
        <MenuItem key={input} value={input}>
          <TranslationWrapper tDefault={input} tKey={"menukaart.form.algemeen.label." + input} />
        </MenuItem>
      );
    });

  const save: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      setSaving(true);

      const formElements = form.current?.elements ?? [];

      const data: {
        ids: number[];
        values: { field: ValidInputNameKey; value: (typeof formData)[ValidInputNameKey] }[];
        images: { name: ValidInputNameKey; data: string | ArrayBuffer | null }[];
      } = {
        ids: props.menukaartIds,
        values: [],
        images: [],
      };

      Array.from(formElements).forEach((e) => {
        if ("name" in e && "value" in e && validInputNames.includes(e.name as ValidInputNameKey)) {
          data.values.push({
            field: e.name as ValidInputNameKey,
            value: e.value,
          });
        }
      });

      /**
       * Add Images
       */
      for (const img in imageList) {
        data.images.push({
          name: img as ValidInputNameKey,
          data: imageList[img as ValidInputNameKey] ?? null,
        });
      }

      axios.post("core/kaarten/mass_edit", data).then((response) => {
        props.toggle();
        props.refreshTable();
        toast.success(response.data.data.message);
        setSaving(false);
      });
    },
    [setSaving, props.toggle, props.refreshTable, props.menukaartIds]
  );

  return (
    <Dialog
      TransitionComponent={Transition}
      aria-describedby={"notice-modal-slide-description"}
      aria-labelledby={"notice-modal-slide-title"}
      fullWidth={true}
      keepMounted
      maxWidth={"lg"}
      onClose={props.toggle}
      open={props.open}
    >
      <form onSubmit={save} ref={form}>
        <DialogTitle className={classes.modalHeader} id={"notice-modal-slide-title"}>
          <Button
            aria-label={"Close"}
            className={classes.modalCloseButton}
            color={"transparent"}
            justIcon
            key={"close"}
            onClick={props.toggle}
          >
            <Close className={classes.modalClose} />
          </Button>
          <h4 className={classes.modalTitle}>
            <TranslationWrapper tDefault={"Menukaarten aanpassen"} tKey={"menukaarten.massedit.modal.header"} />
          </h4>
        </DialogTitle>
        <DialogContent className={classes.modalBody} id={"notice-modal-slide-description"}>
          <GridContainer>
            <GridItem className={classes.selectInputGridItem} sm={12} xs={12}>
              <FormControl className={classes.selectFormControl} fullWidth>
                <Select
                  MenuProps={{
                    className: classes.selectMenu,
                  }}
                  classes={{
                    select: classes.select,
                  }}
                  displayEmpty
                  onChange={(e) => {
                    toggleActiveItem(e.target.value as ValidInputNameKey, true);
                  }}
                  value={""}
                >
                  <MenuItem
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                    disabled
                    key={"empty"}
                    value={""}
                  >
                    <TranslationWrapper
                      tDefault={"Kies wat je wilt aanpassen."}
                      tKey={"menukaarten.massedit.modal.noselect"}
                    />
                  </MenuItem>
                  {inputSelect}
                </Select>
              </FormControl>
            </GridItem>
          </GridContainer>
          {activeItems.naam ? (
            <MassEditRow
              classes={classes}
              input={
                <CustomInput
                  formControlProps={{
                    fullWidth: true,
                  }}
                  id={"naam"}
                  inputProps={{
                    type: "text",
                    name: "naam",
                  }}
                />
              }
              onClick={() => toggleActiveItem("naam", false)}
              tDefault={"Name"}
              tKey={"menukaarten.form.algemeen.label.naam"}
            />
          ) : null}
          {activeItems.sortkey ? (
            <MassEditRow
              classes={classes}
              input={
                <CustomInput
                  formControlProps={{
                    fullWidth: true,
                  }}
                  id={"sortkey"}
                  inputProps={{
                    type: "number",
                    name: "sortkey",
                  }}
                />
              }
              onClick={() => toggleActiveItem("sortkey", false)}
              tDefault={"sortkey"}
              tKey={"menukaarten.form.algemeen.label.sortkey"}
            />
          ) : null}
          {/*{activeItems.imgidbanner ? (*/}
          {/*  <MassEditRow*/}
          {/*    classes={classes}*/}
          {/*    tKey={"menukaarten.form.algemeen.label.header"}*/}
          {/*    tDefault={"Header image"}*/}
          {/*    onClick={() => toggleActiveItem("imgidbanner", false)}*/}
          {/*    input={*/}
          {/*      <input*/}
          {/*        type={"file"}*/}
          {/*        name={"imgidbanner"}*/}
          {/*        accept={"image/png,image/jpeg,image/jpg,image/gif"}*/}
          {/*        onChange={(event) => {*/}
          {/*          const reader = new FileReader();*/}
          {/*          if ("target" in event && event.target.files && (event.target.files?.length ?? -1) > 0) {*/}
          {/*            const targetName = event.target.name;*/}
          {/*            reader.onloadend = () => {*/}
          {/*              setImageList({*/}
          {/*                ...imageList,*/}
          {/*                [targetName]: reader.result,*/}
          {/*              });*/}
          {/*            };*/}
          {/*            reader.readAsDataURL(event.target.files[0]);*/}
          {/*          }*/}
          {/*        }}*/}
          {/*      />*/}
          {/*    }*/}
          {/*  />*/}
          {/*) : null}*/}
          {activeItems.is_actief ? (
            <MassEditRow
              classes={classes}
              input={
                <CustomSwitch
                  checked={formData.is_actief ? formData.is_actief : false}
                  name={"is_actief"}
                  onChange={({ target: { checked } }: { target: { checked: boolean } }) => {
                    setFormData({
                      ...formData,
                      is_actief: checked,
                    });
                  }}
                />
              }
              onClick={() => toggleActiveItem("is_actief", false)}
              tDefault={"Actief"}
              tKey={"menukaarten.form.algemeen.label.is_actief"}
            />
          ) : null}
          {activeItems.disableOrdering ? (
            <MassEditRow
              classes={classes}
              input={
                <CustomSwitch
                  checked={formData.disableOrdering ? formData.disableOrdering : false}
                  name={"disableOrdering"}
                  onChange={({ target: { checked } }: { target: { checked: boolean } }) => {
                    setFormData({
                      ...formData,
                      disableOrdering: checked,
                    });
                  }}
                />
              }
              onClick={() => toggleActiveItem("disableOrdering", false)}
              tDefault={"Disable Ordering"}
              tKey={"menukaarten.form.algemeen.label.disableOrdering"}
            />
          ) : null}
          {activeItems.blocked ? (
            <MassEditRow
              classes={classes}
              input={
                <CustomSwitch
                  checked={formData.blocked ? formData.blocked : false}
                  name={"blocked"}
                  onChange={({ target: { checked } }: { target: { checked: boolean } }) => {
                    setFormData({
                      ...formData,
                      blocked: checked,
                    });
                  }}
                />
              }
              onClick={() => toggleActiveItem("blocked", false)}
              tDefault={"Block in Jamezz"}
              tKey={"menukaarten.form.algemeen.label.blocked"}
            />
          ) : null}
          {activeItems.timeTable ? (
            <MassEditRow
              classes={classes}
              input={<TimeTable data={formData.timeTable ? formData.timeTable : null} inputName={"timeTable"} />}
              onClick={() => toggleActiveItem("timeTable", false)}
              tDefault={"Tijd schema"}
              tKey={"menukaart.form.algemeen.label.timeTable"}
            />
          ) : null}
          {activeItems.fontColor ? (
            <MassEditRow
              classes={classes}
              input={
                <ColorPicker
                  id={"fontColor"}
                  name={"fontColor"}
                  onChange={(colorName: ValidInputNameKey, { hex }: { hex: string }) => {
                    setFormData({
                      ...formData,
                      fontColor: hex,
                    });
                  }}
                  value={formData.fontColor ? formData.fontColor : ""}
                />
              }
              onClick={() => toggleActiveItem("fontColor", false)}
              tDefault={"Fontcolor"}
              tKey={"menukaarten.form.algemeen.label.fontColor"}
            />
          ) : null}
          {activeItems.bgColor ? (
            <MassEditRow
              classes={classes}
              input={
                <ColorPicker
                  id={"bgColor"}
                  name={"bgColor"}
                  onChange={(colorName: ValidInputNameKey, { hex }: { hex: string }) => {
                    setFormData({
                      ...formData,
                      bgColor: hex,
                    });
                  }}
                  value={formData.bgColor ? formData.bgColor : ""}
                />
              }
              onClick={() => toggleActiveItem("bgColor", false)}
              tDefault={"Background colour"}
              tKey={"menukaarten.form.algemeen.label.bgColor"}
            />
          ) : null}
          {activeItems.onOrderscreen ? (
            <MassEditRow
              classes={classes}
              input={
                <FormControl className={classes.selectFormControl} fullWidth>
                  <Select
                    MenuProps={{
                      className: classes.selectMenu,
                    }}
                    classes={{
                      select: classes.select,
                    }}
                    displayEmpty
                    name={"onOrderscreen"}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        onOrderscreen: e.target.value as string,
                      });
                    }}
                  >
                    {kitchenDisplay}
                  </Select>
                </FormControl>
              }
              onClick={() => toggleActiveItem("onOrderscreen", false)}
              tDefault={"Display on kitchen screen"}
              tKey={"producten.form.algemeen.label.onOrderscreen"}
            />
          ) : null}
        </DialogContent>
        <DialogActions className={classes.modalFooter + " " + classes.modalFooterCenter}>
          <SaveButton
            buttonText={props.menukaartIds.length}
            disabled={!canSave()}
            saving={saving}
            tDefault={"item(s) Aanpassen"}
            tKey={"modal.copy.button.mass_edit"}
            type={"submit"}
          />
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default withStyles(MassEdit, (theme) => ({
  ...menukaartenStyle(theme),
  ...jamezzFormStyles(theme),
  ...customSelectStyle(theme),
  ...notificationsStyle(theme),
  ...modalStyle(theme),
}));
